import '../../styles.scoped.css'
import {Typography, Button, TextField, Chip, IconButton} from '@mui/material'
import {Add, Delete} from '@mui/icons-material'
import * as yup from 'yup'
import {emptyUser, optionalPhone, requiredEmail, tooLong, showDraftErrors} from '../../helpers'
import {FormSelect} from '../Branches'
import FieldLabel from '../FieldLabel'

const userSchema = yup.object().shape({
    first_name: yup.string().trim().required('First name is required').max(100, tooLong(100)),
    last_name: yup.string().trim().required('Last name is required').max(100, tooLong(100)),
    email: requiredEmail(255),
    phone: optionalPhone(20),
    role_id: yup.string().required('Choose a role. A user with none can sign in but see nothing.'),
    branch_keys: yup
        .array()
        .of(yup.string().required('Select a branch'))
        .min(1, 'Choose at least one branch')
        .test('distinct', 'This branch is already selected', function (keys) {
            const index = (keys || []).findIndex((key, position) => key && keys.indexOf(key) !== position)
            return index === -1 || this.createError({path: `branch_keys[${index}]`, message: 'This branch is already selected'})
        }),
})

export const fullName = user =>
    [user.first_name, user.last_name]
        .map(part => part?.trim())
        .filter(Boolean)
        .join(' ')

export const branchNames = (user, branches) =>
    user.branch_keys
        .map(key => branches.find(branch => branch.key === key)?.branch_name)
        .filter(Boolean)
        .join(', ')

export const roleName = (roles, id) => {
    const role = roles.find(item => item.id === id)
    return role?.display_name || role?.name || ''
}

export default ({form, roles, branches}) => {
    const users = form.watch('users') || []
    const draftOpen = form.watch('draftOpen')
    const placements = form.watch('draft.branch_keys') || []
    const errors = form.formState.errors
    const draftErrors = errors?.draft
    const roleOptions = roles.map(role => ({value: role.id, label: role.display_name || role.name}))
    const branchOptions = branches.map(branch => ({value: branch.key, label: branch.branch_name}))
    const adminRole = roles.find(role => role.name === 'admin') || roles[0]

    const openDraft = () => {
        form.setValue('draft', {...emptyUser(), role_id: adminRole?.id || ''})
        form.setValue('draftOpen', true)
    }

    const closeDraft = () => {
        form.setValue('draft', {...emptyUser(), role_id: adminRole?.id || ''})
        form.setValue('draftOpen', false)
        form.clearErrors(['draft', 'draftOpen', 'users'])
    }

    const save = async () => {
        const draft = form.getValues('draft')
        form.clearErrors('draft')
        try {
            await userSchema.validate(draft, {abortEarly: false})
        } catch (error) {
            showDraftErrors(form, 'draft', error)
            return
        }
        // An email belongs to one account across every organization.
        const email = draft.email.trim().toLowerCase()
        if (users.some(user => user.email.trim().toLowerCase() === email)) {
            form.setError('draft.email', {message: 'This email is already used by another user here'})
            return
        }
        form.setValue('users', [...users, draft])
        closeDraft()
    }

    const setPlacements = keys => form.setValue('draft.branch_keys', keys)

    return (
        <>
            <Typography variant="h6" color="text.primary">
                Users
            </Typography>
            <Typography className="panel-subtitle" variant="body2" color="text.secondary">
                Add the people who'll manage this organization in Careviah. Each receives an email with a link to set their own password.
            </Typography>
            <Button
                variant="outlined"
                fullWidth
                startIcon={<Add />}
                disabled={!!draftOpen}
                style={{marginBottom: 20, borderRadius: 20}}
                onClick={openDraft}
            >
                Add another user
            </Button>
            {draftOpen && (
                <div className="card">
                    <div className="form">
                        <div className="form-left field">
                            <FieldLabel required>First Name</FieldLabel>
                            <TextField
                                placeholder="Enter the first name"
                                fullWidth
                                {...form.register('draft.first_name')}
                                error={!!draftErrors?.first_name}
                                helperText={draftErrors?.first_name?.message}
                            />
                        </div>
                        <div className="form-right field">
                            <FieldLabel required>Last Name</FieldLabel>
                            <TextField
                                placeholder="Enter the last name"
                                fullWidth
                                {...form.register('draft.last_name')}
                                error={!!draftErrors?.last_name}
                                helperText={draftErrors?.last_name?.message}
                            />
                        </div>
                    </div>
                    <div className="form">
                        <div className="form-left field">
                            <FieldLabel required>Email</FieldLabel>
                            <TextField
                                placeholder="Enter the email"
                                fullWidth
                                {...form.register('draft.email')}
                                error={!!draftErrors?.email}
                                helperText={draftErrors?.email?.message}
                            />
                        </div>
                        <div className="form-right field">
                            <FieldLabel>Phone Number</FieldLabel>
                            <TextField
                                placeholder="Enter the phone number"
                                fullWidth
                                {...form.register('draft.phone')}
                                error={!!draftErrors?.phone}
                                helperText={draftErrors?.phone?.message}
                            />
                        </div>
                    </div>
                    <div className="field">
                        <FieldLabel required>Role</FieldLabel>
                        <FormSelect
                            form={form}
                            name="draft.role_id"
                            options={roleOptions}
                            empty="Select the role for this user"
                            error={!!draftErrors?.role_id}
                            helperText={draftErrors?.role_id?.message}
                        />
                    </div>
                    <div className="section">
                        <Typography className="section-label" variant="overline" color="text.secondary">
                            BRANCH PLACEMENT
                        </Typography>
                        {placements.map((key, position) => (
                            <div key={position}>
                                <FieldLabel required>Branch Placement</FieldLabel>
                                <div className="placement-row">
                                    <div className="picker">
                                        <FormSelect
                                            form={form}
                                            name={`draft.branch_keys.${position}`}
                                            options={branchOptions}
                                            empty="Select branch"
                                            error={!!draftErrors?.branch_keys?.[position]}
                                            helperText={draftErrors?.branch_keys?.[position]?.message}
                                        />
                                    </div>
                                    <div className="placement-side">
                                        {position === 0 ? (
                                            <Chip label="Primary" color="primary" size="small" />
                                        ) : (
                                            <IconButton
                                                aria-label="Remove branch placement"
                                                onClick={() => setPlacements(placements.filter((_, i) => i !== position))}
                                            >
                                                <Delete color="error" />
                                            </IconButton>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {typeof draftErrors?.branch_keys?.message === 'string' && (
                            <Typography variant="body2" color="error">
                                {draftErrors.branch_keys.message}
                            </Typography>
                        )}
                        {placements.length < branches.length && (
                            <Button startIcon={<Add />} onClick={() => setPlacements([...placements, ''])}>
                                Add branch
                            </Button>
                        )}
                    </div>
                    <div className="draft-actions" style={{marginTop: 16}}>
                        <Button variant="contained" onClick={save}>
                            Add user
                        </Button>
                        <Button variant="outlined" onClick={closeDraft}>
                            Cancel
                        </Button>
                    </div>
                    {errors?.draftOpen?.message && (
                        <Typography variant="body2" color="error" style={{marginTop: 8}}>
                            {errors.draftOpen.message}
                        </Typography>
                    )}
                </div>
            )}
            {typeof errors?.users?.message === 'string' && (
                <Typography variant="body2" color="error">
                    {errors.users.message}
                </Typography>
            )}
            {users.map((user, position) => (
                <div className="item" key={`${user.email}-${position}`}>
                    <div className="item-info">
                        <Typography variant="h9" color="primary">
                            {fullName(user)}
                            <Typography component="span" variant="body2" color="text.secondary">
                                {' '}
                                · {roleName(roles, user.role_id)}
                            </Typography>
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {[user.email.trim(), user.phone?.trim()].filter(Boolean).join(' | ')}
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            Branches: {branchNames(user, branches)}
                        </Typography>
                    </div>
                    <IconButton
                        aria-label={`Remove ${fullName(user)}`}
                        onClick={() =>
                            form.setValue(
                                'users',
                                users.filter((_, i) => i !== position),
                            )
                        }
                    >
                        <Delete color="error" />
                    </IconButton>
                </div>
            ))}
        </>
    )
}
